import { CertificateType, ICertificate } from "../../../models/certificate";
import { getLatinNonLatinCombined, IIDDataSet } from "../../../models/crewMember";
import euFlag from "../../../images/Flag_of_Europe.svg.png";
import { CrewMemberImage } from "../../CrewMember/CrewMemberImage";
import { AuthorizationType, authorizationTypeOptions } from "../../../models/authorization";
import { TranslateProps } from "../../../i18n/TranslateProps";
import { formatDate, riverRangeProps } from "../../../models/common";
import "./EuropeanCertificateView.css";

type Props = {
    certificate: ICertificate;
    idDataSet: IIDDataSet;
    modifyable: boolean;
    qrBase64: string;
};

export const EuropeanCertificateView = ({
    certificate,
    idDataSet,
    modifyable,
    qrBase64,
}: Props) => {
    const lngOrPassenger =
        certificate.type === CertificateType.UcqLngExpert ||
        certificate.type === CertificateType.UcqPassengerNavigationExpert;
    const issuingCountry = certificate.issuingAuthority.includes("AT")
        ? "REPUBLIK ÖSTERREICH"
        : certificate.issuingAuthority.includes("CH")
        ? "SCHWEIZERISCHE EIDGENOSSENSCHAFT"
        : certificate.issuingAuthority;

    return (
        <div className={`ucq-frame ${lngOrPassenger ? "LNG-frame" : ""}`}>
            <p style={{ paddingLeft: "5px", fontSize: "11px", fontWeight: "bold" }}>
                {issuingCountry}
            </p>
            <div className="headlineWithFlag">
                <div>
                    <p
                        style={{
                            fontSize: "12px",
                            marginBottom: "0px",
                            fontWeight: "bold",
                            paddingTop: "3px",
                        }}
                    >
                        BEFÄHIGUNGSZEUGNIS DER EUROPÄISCHEN UNION FÜR DIE BINNENSCHIFFFAHRT
                    </p>
                    <p style={{ fontWeight: "bold" }}>
                        {certificate.type === CertificateType.UcqBoatmaster
                            ? "Schiffsführer" + (certificate.gender === 2 ? "in" : "")
                            : certificate.type === CertificateType.UcqLngExpert
                            ? "LNG-Sachkundige"
                            : "Sachkundige für die Fahrgastschifffahrt"}
                    </p>
                </div>
                <img
                    className={`${lngOrPassenger ? "LNG-img" : ""}`}
                    alt="European Union flag"
                    src={euFlag}
                ></img>
            </div>
            <div className="middle">
                <div className="middleLeft">
                    <p style={{ paddingTop: "50px", marginBottom: "0px" }}>
                        1. &nbsp;&nbsp;{" "}
                        {getLatinNonLatinCombined(idDataSet.nonLatinLastName, idDataSet.lastName)}
                    </p>
                    <p className="position">
                        2. &nbsp;&nbsp;{" "}
                        {getLatinNonLatinCombined(idDataSet.nonLatinFirstName, idDataSet.firstName)}
                    </p>
                    <div className={`${lngOrPassenger ? "LNG-flex" : ""}`}>
                        <p className="position">3a. &nbsp; {formatDate(idDataSet.dateOfBirth)}</p>
                        <p className={`position ${lngOrPassenger ? "move-right" : ""}`}>
                            3b. &nbsp;{" "}
                            {getLatinNonLatinCombined(
                                idDataSet.nonLatinPlaceOfBirth,
                                idDataSet.placeOfBirth
                            )}
                        </p>
                    </div>
                    <p style={{ paddingBottom: "40px" }}>4. &nbsp;&nbsp; {idDataSet.cid}</p>
                </div>
                <div className={`photo ${lngOrPassenger ? "LNG-photo" : ""}`}>
                    <CrewMemberImage imageBase64={idDataSet.imageBase64} />
                    {lngOrPassenger ? (
                        <p className="LNG-sixth-position">
                            <span>
                                6.&nbsp;&nbsp;
                                {modifyable ? <em>Wird zugeteilt</em> : certificate.qualificationID}
                            </span>
                        </p>
                    ) : null}
                </div>
            </div>
            {certificate.type === CertificateType.UcqBoatmaster && (
                <p className="position">
                    <span>
                        6.&nbsp;&nbsp;
                        {modifyable ? <em>Wird zugeteilt</em> : certificate.qualificationID}
                    </span>
                </p>
            )}
            <div className={`${lngOrPassenger ? "LNG-flex" : ""}`}>
                <p className="position">7. &nbsp;&nbsp; {formatDate(certificate.startDate)}</p>
                <p className={`position ${lngOrPassenger ? "move-right" : ""}`}>
                    8. &nbsp;&nbsp; {formatDate(certificate.endDate)}
                </p>
            </div>
            <p>9. &nbsp;&nbsp; {certificate.issuingAuthority}</p>
            <div className={`${lngOrPassenger ? "none" : ""}`}>
                <span>
                    10. &nbsp;&nbsp;{" "}
                    {certificate.authorizations
                        .sort((a, b) => a.type - b.type)
                        .map(a => {
                            return (
                                TranslateProps(authorizationTypeOptions).find(
                                    x => x.value === a.type
                                )?.code ?? ""
                            );
                        })
                        .filter(x => x !== "S")
                        .join(", ")}
                </span>
                <br />
                {certificate.authorizations.some(a => a.type === AuthorizationType.S) ? (
                    <>
                        <span>
                            {constructRiverKilometersWithAuthorization(certificate).map(x => (
                                <span key={x}>
                                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                    {x}
                                    <br />
                                </span>
                            ))}
                        </span>
                        <p></p>
                    </>
                ) : (
                    <p></p>
                )}
                <p style={{ paddingBottom: "50px" }}>
                    11. {writeUcqCertificateRestrictions(certificate)}
                </p>
            </div>
            <div className="certificate-qrcode-container">
                <img src={`data:image/jpg;base64,${qrBase64}`} alt="2D-Barcode" />
            </div>
        </div>
    );
};

const writeUcqCertificateRestrictions = (certificate: ICertificate): string => {
    const resultArray: string[] = [];
    certificate.restrictions
        .sort((a, b) => a - b)
        .forEach(r => {
            let rest = r.toString().padStart(2, "0");
            if (r === 7) rest += " " + certificate.limitedToOneCraftDescription;
            else if (r === 8) rest += " " + certificate.limitedAreaDescription;
            else if (r === 9) rest += " " + certificate.limitedTaskDescription;
            resultArray.push(rest);
        });
    if (!!certificate.restriction96) resultArray.push(certificate.restriction96);
    return resultArray.join(", ");
};

const constructRiverKilometersWithAuthorization = (certificate: ICertificate) => {
    const resultArray: string[] = [];
    certificate.authorizations
        .map(a => a.riverKilometers)
        .flat()
        .filter((value, index, array) => array.indexOf(value) === index) // remove duplicate streches
        .sort((a, b) => a - b)
        .forEach(r => {
            const riverKilometer = riverRangeProps.find(x => x.value === r)?.text;
            resultArray.push(`${riverKilometer} ${certificate.qualificationID}`);
        });
    return resultArray;
};
